import { useCallback, useState } from 'react';
import { Settings } from '@drift-labs/icons';
import SideDrawer from 'src/components/Drawers/BaseDrawers/SideDrawer/SideDrawer';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import Button from 'src/components/primitives/Button/Button';
import NotificationSettings from './NotificationSettings';
import FundingRateAlertList, {
	FundingRateAlertListTitle,
} from './FundingRateAlertList';
import CreateAlertForm from './CreateAlertForm';
import { SettingsView } from './types';
import { useFundingRateAlerts } from './hooks/useFundingRateAlerts';
import { useCreateAlertForm } from './hooks/useCreateAlertForm';

type DrawerView = 'list' | SettingsView['screen'];

export default function NotificationDrawer() {
	const [view, setView] = useState<DrawerView>('list');

	const handleAlertCreated = useCallback(() => {
		setView('fundingRateAlertList');
	}, []);

	const createAlertForm = useCreateAlertForm(handleAlertCreated);

	const handleNavigate = useCallback((settingsView: SettingsView) => {
		setView(settingsView.screen);
	}, []);

	const handleBack = useCallback(() => {
		if (view === 'createAlert') {
			setView('fundingRateAlertList');
			createAlertForm.reset();
		} else if (view === 'fundingRateAlertList') {
			setView('settings');
		} else if (view === 'settings') {
			setView('list');
		}
	}, [view, createAlertForm.reset]);

	const handleClose = useCallback(() => {
		setView('list');
		createAlertForm.reset();
	}, [createAlertForm.reset]);

	const settingsEndElement =
		view === 'list' ? (
			<IconButton
				variant="tertiary"
				size="lg"
				icon={Settings}
				onClick={() => setView('settings')}
			/>
		) : undefined;

	return (
		<SideDrawer.Container drawerName="notifications" onClose={handleClose}>
			<DrawerTitle
				view={view}
				onBack={handleBack}
				endElement={settingsEndElement}
			/>
			<DrawerBody
				view={view}
				onNavigate={handleNavigate}
				createAlertForm={createAlertForm}
			/>
			<DrawerFooter
				view={view}
				onCreateNew={() => setView('createAlert')}
				createAlertForm={createAlertForm}
			/>
		</SideDrawer.Container>
	);
}

function DrawerTitle({
	view,
	onBack,
	endElement,
}: {
	view: DrawerView;
	onBack: () => void;
	endElement?: React.ReactNode;
}) {
	const { alerts } = useFundingRateAlerts();

	switch (view) {
		case 'settings':
			return (
				<SideDrawer.Title showBack onBack={onBack}>
					Notification Settings
				</SideDrawer.Title>
			);
		case 'fundingRateAlertList':
			return (
				<SideDrawer.Title showBack onBack={onBack}>
					<FundingRateAlertListTitle alertCount={alerts.length} />
				</SideDrawer.Title>
			);
		case 'createAlert':
			return (
				<SideDrawer.Title showBack onBack={onBack}>
					Create Alert
				</SideDrawer.Title>
			);
		case 'list':
		default:
			return (
				<SideDrawer.Title endElement={endElement}>
					Notifications
				</SideDrawer.Title>
			);
	}
}

function DrawerBody({
	view,
	onNavigate,
	createAlertForm,
}: {
	view: DrawerView;
	onNavigate: (v: SettingsView) => void;
	createAlertForm: ReturnType<typeof useCreateAlertForm>;
}) {
	switch (view) {
		case 'settings':
			return (
				<SideDrawer.Body>
					<NotificationSettings onNavigate={onNavigate} />
				</SideDrawer.Body>
			);
		case 'fundingRateAlertList':
			return (
				<SideDrawer.Body>
					<FundingRateAlertList />
				</SideDrawer.Body>
			);
		case 'createAlert':
			return (
				<SideDrawer.Body>
					<CreateAlertForm form={createAlertForm} />
				</SideDrawer.Body>
			);
		case 'list':
		default:
			return (
				<SideDrawer.Body>
					<div className="flex flex-1 items-center justify-center">
						<p className="typo-b4 text-text-tertiary">No notifications yet</p>
					</div>
				</SideDrawer.Body>
			);
	}
}

function DrawerFooter({
	view,
	onCreateNew,
	createAlertForm,
}: {
	view: DrawerView;
	onCreateNew: () => void;
	createAlertForm: ReturnType<typeof useCreateAlertForm>;
}) {
	const { canCreate } = useFundingRateAlerts();

	if (view === 'fundingRateAlertList') {
		return (
			<SideDrawer.Footer>
				<Button
					size="lg"
					className="w-full"
					disabled={!canCreate}
					onClick={onCreateNew}
				>
					Create Alert
				</Button>
			</SideDrawer.Footer>
		);
	}

	if (view === 'createAlert') {
		return (
			<SideDrawer.Footer>
				<Button
					size="lg"
					className="w-full"
					disabled={!createAlertForm.isValid}
					onClick={createAlertForm.handleSubmit}
				>
					Create Alert
				</Button>
			</SideDrawer.Footer>
		);
	}

	return null;
}
